import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { WatchDemoModal } from "@/components/WatchDemoModal";
import { logEvent } from "@/lib/analytics";
import { exploreSampleAndGo } from "@/lib/sampleData";

const DISMISS_KEY = "recoveros_inapp_demo_fab_dismissed";
// Routes where the FAB must never appear (payment / import / billing / focus screens).
const HIDDEN_PREFIXES = ["/import", "/billing", "/payments", "/payment-success", "/subscription", "/change-password", "/welcome"];

// In-app floating "Watch Demo" button for trial/collections users. Additive — rendered inside Layout.
export function InAppDemoFab() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === "1"; } catch (e) { return false; }
  });

  const allowedRole = ["client_admin", "manager", "agent"].includes(user?.role);
  const routeHidden = HIDDEN_PREFIXES.some((p) => location.pathname.startsWith(p));
  if (!allowedRole || routeHidden || dismissed) return null;

  const dismiss = (e) => {
    e.stopPropagation();
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch (err) { /* noop */ }
    setDismissed(true);
  };

  return (
    <>
      <AnimatePresence>
        <motion.div
          className="fixed bottom-6 left-6 z-[110]"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
          transition={{ type: "spring", stiffness: 300, damping: 24 }}
        >
          <div className="relative">
            <button
              onClick={() => { logEvent("demo_fab_clicked", { source: "in-app" }); setOpen(true); }}
              className="inline-flex items-center gap-2 rounded-full bg-[#0B3D91] px-5 py-3 text-sm font-bold text-white shadow-xl shadow-[#0B3D91]/30 transition-all hover:bg-[#0a347d] hover:-translate-y-0.5"
              data-testid="inapp-demo-fab"
              aria-label="Watch 4-minute demo"
            >
              🎥 Watch 4-Min Demo
            </button>
            <button
              onClick={dismiss} aria-label="Dismiss demo button" data-testid="inapp-demo-fab-dismiss"
              className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-slate-700 text-white hover:bg-slate-900"
            >
              <X size={11} strokeWidth={3} />
            </button>
          </div>
        </motion.div>
      </AnimatePresence>
      <WatchDemoModal
        open={open} onOpenChange={setOpen} source="in-app-fab"
        onSample={() => exploreSampleAndGo(toast)}
        onImport={() => navigate("/import")}
        onBookDemo={() => navigate("/demo")}
      />
    </>
  );
}
